import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import SubscriptionTierComparisonComponent
  from '@fuww/library/src/SubscriptionTierComparison';
import messages from '../../lib/messages.json';
import LinkAndAnchor from '../LinkAndAnchor';
import { useSiteContext } from '../SiteContext';
import withQuery from '../../lib/withQuery';
import bundlesQuery
  from '../../lib/queries/dashboard/bundlesQuery.graphql';
import formatBundlePrices from '../../lib/formatBundlePrices';
import {
  AccountType,
  BundlesQuery,
  BundlesQueryVariables,
} from '../../lib/graphql/api/graphql';

const MonthlyLink = ({ children }: { children: React.ReactNode }) => (
  <LinkAndAnchor route="subscribe" params={{ billing: 'monthly' }}>
    {children}
  </LinkAndAnchor>
);

const YearlyLink = ({ children }: { children: React.ReactNode }) => (
  <LinkAndAnchor route="subscribe" params={{ billing: 'yearly' }}>
    {children}
  </LinkAndAnchor>
);

const ContactSalesLink = (
  { children }: { children: React.ReactNode },
) => (
  <LinkAndAnchor href="mailto:subscriptions@fashionunited.com">
    {children}
  </LinkAndAnchor>
);

interface SubscriptionTierComparisonProperties {
  data: BundlesQuery | undefined;
}

export const SubscriptionTierComparison = ({
  data,
}: SubscriptionTierComparisonProperties) => {
  const intl = useIntl();
  const { locale } = useSiteContext();

  const formattedPrices = useMemo(
    () => formatBundlePrices(data, locale),
    [data, locale],
  );

  const sharedFeatures = [
    intl.formatMessage(
      messages['subscriptionCallToAction.exclusiveAccess'],
    ),
    intl.formatMessage(
      messages['subscriptionCallToAction.insiderForecasts'],
    ),
    intl.formatMessage(
      messages['subscriptionCallToAction.directInsights'],
    ),
  ];

  const perMonth = intl.formatMessage(
    messages['subscriptionCallToAction.perMonthAbbreviation'],
  );

  return (
    <SubscriptionTierComparisonComponent
      headingMessage={intl.formatMessage(
        messages['newsSubscription.tierComparison.heading'],
      )}
      monthlyTier={{
        title: intl.formatMessage(
          messages['newsSubscription.tier.monthly.title'],
        ),
        priceLabel: formattedPrices.monthly,
        priceDenominator: perMonth,
        billingDescription: intl.formatMessage(
          messages['subscriptionCallToAction.billedMonthly'],
        ),
        features: sharedFeatures,
      }}
      yearlyTier={{
        title: intl.formatMessage(
          messages['newsSubscription.tier.yearly.title'],
        ),
        priceLabel: formattedPrices.annualMonthlyEquivalent,
        priceDenominator: perMonth,
        billingDescription: intl.formatMessage(
          messages['subscriptionCallToAction.billedAnnually'],
          { amount: formattedPrices.annual },
        ),
        savingsMessage: intl.formatMessage(
          messages['subscriptionCallToAction.savePerYear'],
          { amount: formattedPrices.annualSavings },
        ),
        features: sharedFeatures,
      }}
      companyTier={{
        title: intl.formatMessage(
          messages['newsSubscription.tier.companyWide.title'],
        ),
        priceLabel: intl.formatMessage(
          messages['newsSubscription.tier.companyWide.priceLabel'],
        ),
        billingDescription: intl.formatMessage(
          messages[
            'newsSubscription.tier.companyWide.description'
          ],
        ),
        features: [
          ...sharedFeatures,
          intl.formatMessage(
            messages[
              'newsSubscription.tier.feature.teamManagement'
            ],
          ),
          intl.formatMessage(
            messages[
              'newsSubscription.tier.feature.centralBilling'
            ],
          ),
          intl.formatMessage(
            messages[
              'newsSubscription.tier.feature.prioritySupport'
            ],
          ),
        ],
      }}
      recommendedBadgeMessage={intl.formatMessage(
        messages['newsSubscription.tier.companyWide.badge'],
      )}
      subscribeButtonLabel={intl.formatMessage(
        messages['newsSubscription.buttonLabel'],
      )}
      contactSalesButtonLabel={intl.formatMessage(
        messages['newsSubscription.contactSales'],
      )}
      MonthlyLinkComponent={MonthlyLink}
      YearlyLinkComponent={YearlyLink}
      ContactSalesLinkComponent={ContactSalesLink}
    />
  );
};

const SubscriptionTierComparisonWithQuery = withQuery<
  BundlesQuery,
  BundlesQueryVariables,
  true
>(SubscriptionTierComparison, { renderAlways: true });

const SubscriptionTierComparisonDefault = () => (
  <SubscriptionTierComparisonWithQuery
    query={bundlesQuery}
    variables={{
      accountType: AccountType.PERSONAL,
    }}
    errorMessage="Error fetching bundle prices"
    loader={null}
  />
);

export default SubscriptionTierComparisonDefault;
